import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Col, message, Row } from "antd";
import * as Yup from "yup";
import "../../assets/scss/login-style.scss";

import { useNavigate, useSearchParams } from "react-router-dom";
import {
  resetPassword,
  getRefreshToken,
  confirmResetPassword,
} from "../server/Auth";
import { getRefreshTokenSession } from "../utility/common/session-store";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get("token") || "");

  const ResetPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("Incorrect Email format"))
      .required(t("Please provide your Email")),
  });

  const ResetPasswordConfirmSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(8, t("Password must be at least 8 characters"))
      .required(t("Please enter your new password")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], t("Passwords must match"))
      .required(t("Please confirm your password")),
  });

  const resetPasswordSuccess = () => {
    messageApi.success({
      content: t("Reset Password Email Sent!"),
    });
  };

  const resetPasswordConfirmSuccess = () => {
    messageApi.success({
      content: t("Password Reset Successfully!"),
    });
    setTimeout(() => {
      navigate("/");
    }, 1500);
  };

  const resetPasswordError = () => {
    messageApi.error({
      content: t("Reset Password Failed!"),
    });
  };

  const handleSubmitEmail = (email: string) => {
    resetPassword(email)
      .then(async (res) => {
        if (res?.data?.msg === "password reset email sent") {
          resetPasswordSuccess();
        } else {
          resetPasswordError();
        }
      })
      .catch((error) => {
        console.error("Reset password error:", error);
        resetPasswordError();
      });
  };

  const handlePasswordReset = (token: string, newPassword: string) => {
    confirmResetPassword({
      token,
      new_password: newPassword,
    })
      .then(async (res) => {
        if (
          res?.data?.code === 0 &&
          res?.data?.msg === "Password reset successfully"
        ) {
          resetPasswordConfirmSuccess();
          setToken("");
        } else {
          resetPasswordError();
        }
      })
      .catch((error) => {
        console.error("Reset password error:", error);
        resetPasswordError();
      });
  };

  return (
    <>
      {contextHolder}
      <div className="login-page">
        <main className="login-container">
          <div className="login-card">
            <div className="login-card-left">
              <div className="logo" />
            </div>
            <div className="login-card-right">
              <div className="right-side-top">
                <p>
                  {t("Already have an account?")} &nbsp;
                  <button
                    className="sign-up-btn"
                    onClick={() => navigate("/login")}
                  >
                    {t("Log In")}
                  </button>
                </p>
              </div>
              <div className="right-side-middle">
                <h1>{t("Welcome to NeuroPlus!")}</h1>
                <h3>{t("Please enter your email to reset password")}</h3>

                {token == "" ? (
                  <div className="form">
                    <Formik
                      validateOnMount={true}
                      enableReinitialize={true}
                      validationSchema={ResetPasswordSchema}
                      initialValues={{ email: "" }}
                      onSubmit={(values, { setSubmitting }) => {
                        handleSubmitEmail(values.email);
                        setSubmitting(false);
                      }}
                    >
                      {({ errors, touched }) => (
                        <Form>
                          <Row gutter={[0, 24]}>
                            <Col span={12}>
                              <div className="label-wrapper">
                                <label htmlFor="email">{t("E-Mail")}</label>
                                <Field name="email" type="email" />
                                <div className="form-error-msg">
                                  <ErrorMessage name="email" />
                                </div>
                              </div>
                            </Col>
                          </Row>
                          <Row gutter={[0, 24]}>
                            <Col span={12}>
                              <button className="sign-up-btn" type="submit">
                                {t("Reset Password")}
                              </button>
                            </Col>
                          </Row>
                        </Form>
                      )}
                    </Formik>
                  </div>
                ) : (
                  <div className="form">
                    <Formik
                      validateOnMount={true}
                      enableReinitialize={true}
                      validationSchema={ResetPasswordConfirmSchema}
                      initialValues={{ newPassword: "", confirmPassword: "" }}
                      onSubmit={(values, { setSubmitting }) => {
                        handlePasswordReset(token, values.newPassword);
                        setSubmitting(false);
                      }}
                    >
                      {({ errors, touched }) => (
                        <Form>
                          <Row gutter={[0, 24]}>
                            <Col span={12}>
                              <div className="label-wrapper">
                                <label htmlFor="newPassword">
                                  {t("New Password")}
                                </label>
                                <Field name="newPassword" type="password" />
                                <div className="form-error-msg">
                                  <ErrorMessage name="newPassword" />
                                </div>
                              </div>
                            </Col>
                          </Row>
                          <Row gutter={[0, 24]}>
                            <Col span={12}>
                              <div className="label-wrapper">
                                <label htmlFor="confirmPassword">
                                  {t("Confirm Password")}
                                </label>
                                <Field name="confirmPassword" type="password" />
                                <div className="form-error-msg">
                                  <ErrorMessage name="confirmPassword" />
                                </div>
                              </div>
                            </Col>
                          </Row>
                          <Row gutter={[0, 24]}>
                            <Col span={12}>
                              <button className="sign-up-btn" type="submit">
                                {t("Reset Password")}
                              </button>
                            </Col>
                          </Row>
                        </Form>
                      )}
                    </Formik>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ResetPassword;
